import React from 'react';

// global unique key for every gauge (needed for SVG groups to stay separated)
let uniqueId = 0;

export interface GaugeProps {
  label: string;
  min: number;
  max: number;
  value: number;
  width: number;
  height: number;
  minMaxLabelsOffset: number;
  color: string;
  backgroundColor: string;
  topLabelStyle: any;
  valueLabelStyle: any;
  minMaxLabelStyle: any;
  valueFormatter: (value: number) => string;
}

export default class Gauge extends React.Component<GaugeProps> {
  static defaultProps = {
    label: 'React SVG Gauge',
    min: 0,
    max: 100,
    value: 40,
    width: 400,
    height: 320,
    minMaxLabelsOffset: 25,
    color: '#fe0400',
    backgroundColor: '#edebeb',
    topLabelStyle: {
      textAnchor: 'middle',
      fill: '#999999',
      stroke: 'none',
      fontStyle: 'normal',
      fontVariant: 'normal',
      fontWeight: 'bold',
      fontStretch: 'normal',
      lineHeight: 'normal',
      fillOpacity: 1
    },
    valueLabelStyle: {
      textAnchor: 'middle',
      fill: '#010101',
      stroke: 'none',
      fontStyle: 'normal',
      fontVariant: 'normal',
      fontWeight: 'bold',
      fontStretch: 'normal',
      lineHeight: 'normal',
      fillOpacity: 1
    },
    minMaxLabelStyle: {
      textAnchor: 'middle',
      fill: '#999999',
      stroke: 'none',
      fontStyle: 'normal',
      fontVariant: 'normal',
      fontWeight: 'normal',
      fontStretch: 'normal',
      fontSize: 18,
      lineHeight: 'normal',
      fillOpacity: 1
    },
    valueFormatter: (value: number) => `${value}`
  };

  _getPathValues = (value: number) => {
    let   max = this.props.max;
    const min = this.props.min;
    if (value < min) value = min;
    if (value > max) value = max;
    if (min >= max) max = min + 1;

    const dx = 0;
    const dy = 0;

    const alpha = (1 - (value - min) / (max - min)) * Math.PI;
    const Ro = this.props.width / 2 - this.props.width / 10;
    const Ri = Ro - this.props.width / 13.3333333333333;

    const Cx = this.props.width / 2 + dx;
    const Cy = this.props.height / 1.25 + dy;

    const Xo = this.props.width / 2 + dx + Ro * Math.cos(alpha);
    const Yo = this.props.height - (this.props.height - Cy) - Ro * Math.sin(alpha);
    const Xi = this.props.width / 2 + dx + Ri * Math.cos(alpha);
    const Yi = this.props.height - (this.props.height - Cy) - Ri * Math.sin(alpha);

    return { alpha, Ro, Ri, Cx, Cy, Xo, Yo, Xi, Yi };
  };

  _getPath = (value: number) => {
    const { Ro, Ri, Cx, Cy, Xo, Yo, Xi, Yi } = this._getPathValues(value);

    let path = 'M' + (Cx - Ri) + ',' + Cy + ' ';
    path += 'L' + (Cx - Ro) + ',' + Cy + ' ';
    path += 'A' + Ro + ',' + Ro + ' 0 0 1 ' + Xo + ',' + Yo + ' ';
    path += 'L' + Xi + ',' + Yi + ' ';
    path += 'A' + Ri + ',' + Ri + ' 0 0 0 ' + (Cx - Ri) + ',' + Cy + ' ';
    path += 'Z ';

    return path;
  };

  private uniqueFilterId: string;

  render() {
    const topLabelStyle = (this.props.topLabelStyle.fontSize
      ? this.props.topLabelStyle
      : { ...this.props.topLabelStyle, fontSize: (this.props.width / 10) });
    const valueLabelStyle = (this.props.valueLabelStyle.fontSize
      ? this.props.valueLabelStyle
      : { ...this.props.valueLabelStyle, fontSize: (this.props.width / 6) });
    const { minMaxLabelsOffset } = this.props;
    const { Cx, Ro, Ri, Xo, Cy, Xi } = this._getPathValues(this.props.max);
    if (!this.uniqueFilterId) this.uniqueFilterId = 'filter_' + uniqueId++;
    return (
      <svg height='100%' version='1.1' width='100%' xmlns='http://www.w3.org/2000/svg' style={{
        width: this.props.width,
        height: this.props.height,
        overflow: 'hidden',
        position: 'relative',
        left: 0,
        top: 0
      }}>
        <defs>
          <filter id={this.uniqueFilterId}>
            <feOffset dx='0' dy='3' />
            <feGaussianBlur result='offset-blur' stdDeviation='5' />
            <feComposite operator='out' in='SourceGraphic' in2='offset-blur' result='inverse' />
            <feFlood floodColor='black' floodOpacity='0.2' result='color' />
            <feComposite operator='in' in='color' in2='inverse' result='shadow' />
            <feComposite operator='over' in='shadow' in2='SourceGraphic' />
          </filter>
        </defs>
        <path fill={this.props.backgroundColor} stroke='none' d={this._getPath(this.props.max)}
          filter={'url(#' + this.uniqueFilterId + ')'} />
        <path fill={this.props.color} stroke='none' d={this._getPath(this.props.value)}
          filter={'url(#' + this.uniqueFilterId + ')'} />
        <text x={this.props.width / 2} y={this.props.height / 8} textAnchor='middle' style={topLabelStyle}>
          {this.props.label}
        </text>
        <text x={this.props.width / 2} y={this.props.height / 5 * 4} textAnchor='middle' style={valueLabelStyle}>
          {this.props.valueFormatter(this.props.value)}
        </text>
        {this.props.max > 0 ? (
          <>
            <text x={((Cx - Ro) + (Cx - Ri)) / 2} y={Cy + minMaxLabelsOffset} textAnchor='middle' style={this.props.minMaxLabelStyle}>
              {this.props.min}
            </text>
            <text x={(Xo + Xi) / 2} y={Cy + minMaxLabelsOffset} textAnchor='middle' style={this.props.minMaxLabelStyle}>
              {this.props.max}
            </text>
          </>
        ) : null}
      </svg>
    );
  }
}